import { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Plus, Upload, Edit2, Trash2, Star, Eye, EyeOff, Search, ChevronLeft, ChevronRight, ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { examsApi } from '../../services/api';

interface Exam {
  id: string;
  name: string;
  slug: string;
  description: string;
  icon: string;
  totalQuestions: number;
  duration: number;
  passingScore: number;
  isActive: boolean;
  isFeatured: boolean;
  category: string;
  level: string;
  categories: { id: string; name: string }[];
}

type SortKey = 'name' | 'category' | 'totalQuestions' | 'duration' | 'isActive';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 25;

export function ExamsList() {
  const [exams, setExams] = useState<Exam[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const searchQuery = searchParams.get('q') || '';
  const page = parseInt(searchParams.get('page') || '1');
  const sortKey = (searchParams.get('sort') || 'name') as SortKey;
  const sortDir = (searchParams.get('dir') || 'asc') as SortDir;

  const loadExams = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await examsApi.getAll({
        includeInactive: true,
        page,
        limit: PAGE_SIZE,
        search: searchQuery || undefined,
        sort: sortKey,
        dir: sortDir,
      });
      if (data.items) {
        setExams(data.items);
        setTotal(data.total);
      } else {
        const arr = Array.isArray(data) ? data : [];
        setExams(arr);
        setTotal(arr.length);
      }
    } catch {
      toast.error('Erreur lors du chargement des examens');
    } finally {
      setLoading(false);
    }
  }, [page, searchQuery, sortKey, sortDir]);

  useEffect(() => {
    loadExams();
  }, [loadExams]);

  const totalPages = Math.ceil(total / PAGE_SIZE);
  const safePage = Math.max(1, Math.min(page, totalPages || 1));

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    if (key !== 'page') next.set('page', '1');
    setSearchParams(next);
  };

  const handleSearchChange = (value: string) => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set('q', value);
    } else {
      next.delete('q');
    }
    next.set('page', '1');
    searchTimerRef.current = setTimeout(() => {
      setSearchParams(next);
    }, 300);
  };

  const toggleSort = (key: SortKey) => {
    const next = new URLSearchParams(searchParams);
    if (sortKey === key) {
      next.set('dir', sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sort', key);
      next.set('dir', 'asc');
    }
    next.set('page', '1');
    setSearchParams(next);
  };

  const setPage = (p: number) => updateParam('page', p.toString());

  const handleDelete = async (id: string, name: string) => {
    if (!confirm(`Supprimer l'examen "${name}" ? Cette action est irréversible.`)) return;
    try {
      await examsApi.delete(id);
      toast.success('Examen supprimé');
      loadExams();
    } catch {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleToggle = async (exam: Exam) => {
    try {
      await examsApi.update(exam.id, { isActive: !exam.isActive });
      setExams((prev) => prev.map((e) => (e.id === exam.id ? { ...e, isActive: !e.isActive } : e)));
      toast.success(exam.isActive ? 'Examen désactivé' : 'Examen activé');
    } catch {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ArrowUpDown size={13} className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />;
    return sortDir === 'asc' ? <ArrowUp size={13} className="text-emerald-500" /> : <ArrowDown size={13} className="text-emerald-500" />;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Examens</h1>
          <p className="text-gray-500 dark:text-gray-400">
            {total} examen{total !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/admin/exams/import"
            className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors font-medium text-sm"
          >
            <Upload size={16} />
            Importer
          </Link>
          <Link
            to="/admin/exams/new"
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium text-sm"
          >
            <Plus size={16} />
            Nouvel examen
          </Link>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm overflow-hidden">
        {/* Search bar */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Rechercher un examen..."
              defaultValue={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : exams.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 text-gray-400">
            <p className="text-base font-medium">
              {searchQuery ? 'Aucun résultat pour cette recherche' : 'Aucun examen pour l\'instant'}
            </p>
            {!searchQuery && <p className="text-sm mt-1">Créez votre premier examen</p>}
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
                <th className="text-left px-6 py-3">
                  <button onClick={() => toggleSort('name')} className="group flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Examen <SortIcon col="name" />
                  </button>
                </th>
                <th className="text-left px-6 py-3">
                  <button onClick={() => toggleSort('category')} className="group flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Catégorie <SortIcon col="category" />
                  </button>
                </th>
                <th className="text-left px-6 py-3">
                  <button onClick={() => toggleSort('totalQuestions')} className="group flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Questions <SortIcon col="totalQuestions" />
                  </button>
                </th>
                <th className="text-left px-6 py-3">
                  <button onClick={() => toggleSort('duration')} className="group flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Durée <SortIcon col="duration" />
                  </button>
                </th>
                <th className="text-left px-6 py-3">
                  <button onClick={() => toggleSort('isActive')} className="group flex items-center gap-1.5 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 transition-colors">
                    Statut <SortIcon col="isActive" />
                  </button>
                </th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {exams.map((exam) => (
                <tr key={exam.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{exam.icon || '📚'}</span>
                      <div>
                        <p className="font-medium text-gray-900 dark:text-white text-sm">{exam.name}</p>
                        <p className="text-xs text-gray-400">{exam.slug}</p>
                      </div>
                      {exam.isFeatured && (
                        <Star size={14} className="text-yellow-400 fill-yellow-400" />
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-sm text-gray-700 dark:text-gray-300">{exam.category || '—'}</span>
                      {exam.level && <span className="text-xs text-gray-400">{exam.level}</span>}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{exam.totalQuestions}</td>
                  <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{exam.duration} min</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      exam.isActive
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                    }`}>
                      {exam.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleToggle(exam)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        title={exam.isActive ? 'Désactiver' : 'Activer'}
                      >
                        {exam.isActive ? <EyeOff size={16} /> : <Eye size={16} />}
                      </button>
                      <Link
                        to={`/admin/exams/${exam.id}`}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                        title="Modifier"
                      >
                        <Edit2 size={16} />
                      </Link>
                      <button
                        onClick={() => handleDelete(exam.id, exam.name)}
                        className="p-1.5 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Page {safePage} sur {totalPages} — {total} résultat{total !== 1 ? 's' : ''}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(safePage - 1)}
                disabled={safePage <= 1}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                <ChevronLeft size={18} />
              </button>
              <button
                onClick={() => setPage(safePage + 1)}
                disabled={safePage >= totalPages}
                className="p-2 border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
